import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import {
  appointmentCancelApi,
  type CancelAppointmentSummary,
} from '../services/appointmentCancelApi';

type Step = 'loading' | 'review' | 'confirm' | 'done' | 'error' | 'kept';

export function AppointmentCancelPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();

  const [step, setStep] = useState<Step>('loading');
  const [summary, setSummary] = useState<CancelAppointmentSummary | null>(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('appointmentCancel.errors.missingToken'));
      setStep('error');
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await appointmentCancelApi.getSummary(token);
      if (cancelled) return;
      if (!res.success || !res.data) {
        setError(res.message || t('appointmentCancel.errors.loadFailed'));
        setStep('error');
        return;
      }
      setSummary(res.data);
      setStep('review');
    })();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const denialKey = summary?.eligibility.reason
    ? `appointmentCancel.denial.${summary.eligibility.reason}`
    : '';
  const canCancel = Boolean(summary?.eligibility.eligible);

  const handleKeep = () => {
    setStep('kept');
  };

  const handleStartCancel = async () => {
    if (!token || !summary || !summary.eligibility.eligible) return;
    setError('');
    setStep('confirm');
    if (summary.otpRequired && !otpSent) {
      setSubmitting(true);
      const res = await appointmentCancelApi.sendOtp(token);
      setSubmitting(false);
      if (!res.success && res.otpRequired !== false) {
        setError(res.message || t('appointmentCancel.errors.otpFailed'));
        return;
      }
      setOtpSent(true);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !summary || !summary.eligibility.eligible) return;
    setError('');
    if (reason.trim().length < 3) {
      setError(t('appointmentCancel.errors.reasonRequired'));
      return;
    }
    if (summary.otpRequired && otp.trim().length !== 6) {
      setError(t('appointmentCancel.errors.otpRequired'));
      return;
    }

    setSubmitting(true);
    const res = await appointmentCancelApi.confirm(token, {
      reason: reason.trim(),
      ...(summary.otpRequired ? { otp: otp.trim() } : {}),
    });
    setSubmitting(false);

    if (!res.success) {
      setError(res.message || t('appointmentCancel.errors.confirmFailed'));
      return;
    }
    setStep('done');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 pt-24 pb-12 px-4">
      <div className="mx-auto max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-xl sm:p-8">
        <h1 className="text-2xl font-display font-bold text-textPrimary">
          {t('appointmentCancel.title')}
        </h1>
        <p className="mt-2 text-sm text-textSecondary">{t('appointmentCancel.subtitle')}</p>

        {step === 'loading' && (
          <div className="mt-10 flex items-center justify-center gap-2 text-textSecondary">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('appointmentCancel.loading')}
          </div>
        )}

        {step === 'error' && (
          <div className="mt-8 rounded-xl border border-red-300 bg-red-50 p-4 text-red-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <p>{error}</p>
            </div>
            <Link to="/" className="mt-4 inline-block text-sm font-medium text-textPrimary underline">
              {t('appointmentCancel.backHome')}
            </Link>
          </div>
        )}

        {step === 'kept' && (
          <div className="mt-8 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
            <div className="flex items-start gap-2">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">{t('appointmentCancel.keptTitle')}</p>
                <p className="mt-1 text-sm">{t('appointmentCancel.keptBody')}</p>
              </div>
            </div>
            <Link to="/" className="mt-4 inline-block text-sm font-medium text-textPrimary underline">
              {t('appointmentCancel.backHome')}
            </Link>
          </div>
        )}

        {step === 'done' && (
          <div className="mt-8 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
            <div className="flex items-start gap-2">
              <CheckCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">{t('appointmentCancel.doneTitle')}</p>
                <p className="mt-1 text-sm">{t('appointmentCancel.doneBody')}</p>
              </div>
            </div>
            <Link to="/" className="mt-4 inline-block text-sm font-medium text-textPrimary underline">
              {t('appointmentCancel.backHome')}
            </Link>
          </div>
        )}

        {(step === 'review' || step === 'confirm') && summary && (
          <>
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-textPrimary">
              <p>
                <span className="text-textSecondary">{t('appointmentCancel.fields.when')}</span>
                <br />
                <strong>{summary.appointmentDateFormatted}</strong>
              </p>
              <p className="mt-3">
                <span className="text-textSecondary">{t('appointmentCancel.fields.shop')}</span>
                <br />
                <strong>{summary.shopName}</strong>
              </p>
              <p className="mt-3">
                <span className="text-textSecondary">{t('appointmentCancel.fields.service')}</span>
                <br />
                <strong>{summary.serviceName}</strong>
              </p>
            </div>

            {!canCancel && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-400 bg-amber-50 p-4 text-amber-950">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">
                    {t(denialKey, { defaultValue: t('appointmentCancel.errors.notEligible') })}
                  </p>
                  <p className="mt-1 text-amber-900/90">
                    {t('appointmentCancel.notEligibleHint')}
                  </p>
                </div>
              </div>
            )}

            {step === 'review' && (
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={handleKeep} className="btn-secondary flex-1 text-center">
                  {t('appointmentCancel.actions.keep')}
                </button>
                {canCancel ? (
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={handleStartCancel}
                    className="btn-primary flex-1 text-center disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                    ) : (
                      t('appointmentCancel.actions.cancel')
                    )}
                  </button>
                ) : (
                  <Link to="/" className="btn-primary flex-1 text-center">
                    {t('appointmentCancel.backHome')}
                  </Link>
                )}
              </div>
            )}

            {step === 'confirm' && canCancel && (
              <form onSubmit={handleConfirm} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary" htmlFor="cancel-reason">
                    {t('appointmentCancel.fields.reason')}
                  </label>
                  <textarea
                    id="cancel-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="input-field mt-1 w-full"
                    placeholder={t('appointmentCancel.fields.reasonPlaceholder')}
                    required
                  />
                </div>

                {summary.otpRequired && (
                  <div>
                    <label className="block text-sm font-medium text-textPrimary" htmlFor="cancel-otp">
                      {t('appointmentCancel.fields.otp')}
                    </label>
                    <p className="mt-1 text-xs text-textSecondary">{t('appointmentCancel.fields.otpHint')}</p>
                    <input
                      id="cancel-otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className="input-field mt-2 w-full tracking-widest"
                      placeholder="000000"
                    />
                    <button
                      type="button"
                      className="mt-2 text-xs font-medium text-textPrimary underline"
                      disabled={submitting}
                      onClick={async () => {
                        if (!token) return;
                        setSubmitting(true);
                        const res = await appointmentCancelApi.sendOtp(token);
                        setSubmitting(false);
                        if (!res.success) {
                          setError(res.message || t('appointmentCancel.errors.otpFailed'));
                        } else {
                          setOtpSent(true);
                          setError('');
                        }
                      }}
                    >
                      {t('appointmentCancel.actions.resendOtp')}
                    </button>
                  </div>
                )}

                {error && (
                  <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {error}
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('review');
                      setError('');
                    }}
                    className="btn-secondary flex-1"
                  >
                    {t('appointmentCancel.actions.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 rounded-lg border border-red-600 bg-red-700 px-4 py-3 text-sm font-medium text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                    ) : (
                      t('appointmentCancel.actions.confirm')
                    )}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AppointmentCancelPage;
